import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import VariantDisplay from '../components/VariantDisplay';
import StarRating from '../components/StarRating';
import { 
  ArrowLeft, Edit, Trash2, Heart, Calendar, Users, MapPin, Award,
  ChevronLeft, ChevronRight, User, CheckCircle, Shield, AlertCircle,
  Clock, Target, Gift, Briefcase
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { getImageUrl, getFirstImage } from '../utils/imageHelper';
import ListingImage from '../components/ListingImage';

const VolunteeringDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [volunteering, setVolunteering] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [deleting, setDeleting] = React.useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [variantSelections, setVariantSelections] = useState({});
  
  React.useEffect(() => {
    const fetchVolunteering = async () => {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const token = sessionStorage.getItem('token');
        
        const response = await fetch(`${BACKEND_URL}/api/listings/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setVolunteering(data);
        } else {
          navigate('/listings/volunteering');
        }
      } catch (error) {
        console.error('Error:', error);
        navigate('/listings/volunteering');
      } finally {
        setLoading(false);
      }
    };
    
    if (id) fetchVolunteering();
  }, [id, navigate]);
  
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this volunteering opportunity?')) return;
    
    setDeleting(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = sessionStorage.getItem('token');
      
      const response = await fetch(`${BACKEND_URL}/api/listings/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast({ title: 'Success', description: 'Volunteering opportunity deleted successfully' });
        navigate('/listings/volunteering');
      } else {
        toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Error deleting', variant: 'destructive' });
    } finally {
      setDeleting(false);
    }
  };

  const handleVariantSelectionChange = ({ selections }) => {
    setVariantSelections(selections);
  };

  const nextImage = () => {
    if (volunteering?.media?.length > 0) {
      setSelectedImage((prev) => (prev + 1) % volunteering.media.length);
    }
  };

  const prevImage = () => {
    if (volunteering?.media?.length > 0) {
      setSelectedImage((prev) => (prev - 1 + volunteering.media.length) % volunteering.media.length);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Flexible';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      weekday: 'short', year: 'numeric', month: 'short', day: 'numeric'
    });
  };
  
  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading...</div>
        </div>
      </DashboardLayout>
    );
  }
  
  if (!volunteering) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Volunteering opportunity not found</div>
        </div>
      </DashboardLayout>
    );
  }
  
  const data = volunteering.data || {};
  const images = volunteering.media || [];
  
  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 px-4">
          <Button variant="ghost" onClick={() => navigate('/listings/volunteering')} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Volunteering
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate(`/listings/volunteering/edit/${id}`)}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              <Trash2 className="h-4 w-4 mr-2" />
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 px-4">
          
          {/* LEFT COLUMN - Gallery */}
          <div className="space-y-4">
            {/* Main Image */}
            <div className="relative bg-white rounded-xl overflow-hidden border aspect-video">
              {images.length > 0 ? (
                <>
                  <ListingImage src={images[selectedImage]} alt={volunteering.title} className="w-full h-full object-cover" showPlaceholder={false} />
                  {images.length > 1 && (
                    <>
                      <button onClick={prevImage} className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg">
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <button onClick={nextImage} className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg">
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-pink-100 to-purple-100">
                  <Heart className="h-24 w-24 text-pink-300" />
                </div>
              )}
            </div>

            {/* Thumbnails */}
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {images.map((url, index) => (
                  <button
                    key={url.id || url.name || `${url.title || "url"}-${index}`}
                    onClick={() => setSelectedImage(index)}
                    className={`flex-shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 transition-all ${selectedImage === index ? 'border-pink-600 ring-2 ring-pink-200' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <ListingImage src={url} alt={`Thumbnail ${index + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* Tabs */}
            <Tabs defaultValue="description" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="description">About</TabsTrigger>
                <TabsTrigger value="requirements">Requirements</TabsTrigger>
                <TabsTrigger value="benefits">Benefits</TabsTrigger>
              </TabsList>
              
              <TabsContent value="description" className="mt-4">
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{volunteering.description}</p>
                    {data.responsibilities && (
                      <div className="mt-4 pt-4 border-t">
                        <h4 className="font-medium mb-2">Responsibilities</h4>
                        <p className="text-sm text-gray-600 whitespace-pre-wrap">{data.responsibilities}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="requirements" className="mt-4">
                <Card>
                  <CardContent className="pt-4 space-y-4">
                    {data.skills && (
                      <div>
                        <h4 className="font-medium mb-2">Required Skills</h4>
                        <p className="text-sm text-gray-600 whitespace-pre-wrap">{data.skills}</p>
                      </div>
                    )}
                    {data.ageRequirement && (
                      <div>
                        <h4 className="font-medium mb-2">Age Requirement</h4>
                        <p className="text-sm text-gray-600">{data.ageRequirement}</p>
                      </div>
                    )}
                    {data.prerequisites && (
                      <div>
                        <h4 className="font-medium mb-2">Prerequisites</h4>
                        <p className="text-sm text-gray-600 whitespace-pre-wrap">{data.prerequisites}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="benefits" className="mt-4">
                <Card>
                  <CardContent className="pt-4 space-y-4">
                    {data.benefits && (
                      <div className="flex items-start gap-3">
                        <Gift className="h-5 w-5 text-pink-500 mt-0.5" />
                        <div>
                          <h4 className="font-medium">What You'll Get</h4>
                          <p className="text-sm text-gray-600 whitespace-pre-wrap">{data.benefits}</p>
                        </div>
                      </div>
                    )}
                    {data.certificateProvided && (
                      <div className="flex items-start gap-3">
                        <Award className="h-5 w-5 text-yellow-500 mt-0.5" />
                        <div>
                          <h4 className="font-medium">Certificate</h4>
                          <p className="text-sm text-gray-600">Certificate of appreciation will be provided</p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* RIGHT COLUMN - Info */}
          <div className="space-y-6">
            {/* Title & Status */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge variant={volunteering.status === 'active' ? 'default' : 'secondary'}>{volunteering.status}</Badge>
                {data.urgency === 'high' && <Badge className="bg-red-500">Urgent</Badge>}
                {data.isRemote && <Badge variant="outline">Remote</Badge>}
              </div>
              <h1 className="text-2xl font-bold text-gray-900">{volunteering.title}</h1>
              <div className="mt-2">
                <StarRating listingId={id} size="md" showCount={true} fetchData={true} />
              </div>
            </div>

            {/* Organization Info */}
            <div className="flex items-center gap-3 p-3 bg-pink-50 rounded-lg">
              <div className="h-10 w-10 rounded-full bg-pink-100 flex items-center justify-center">
                <Heart className="h-5 w-5 text-pink-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Organization</p>
                <p className="font-medium">{data.organizationName || volunteering.vendor_name || 'Organization'}</p>
              </div>
            </div>

            {/* Category & Cause */}
            <div className="flex gap-2 flex-wrap">
              {data.cause && <Badge variant="outline" className="bg-pink-50">{data.cause}</Badge>}
              {volunteering.category && <Badge variant="outline">{volunteering.category}</Badge>}
            </div>

            {/* Schedule & Location Card */}
            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="pt-4 space-y-3">
                {(data.startDate || data.endDate) && (
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-purple-600" />
                    <div>
                      <p className="font-medium">
                        {formatDate(data.startDate)}
                        {data.endDate && ` - ${formatDate(data.endDate)}`}
                      </p>
                    </div>
                  </div>
                )}
                {data.commitment && (
                  <div className="flex items-center gap-3">
                    <Clock className="h-5 w-5 text-purple-600" />
                    <p className="font-medium">{data.commitment}</p>
                  </div>
                )}
                {(data.location || data.city) && (
                  <div className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-purple-600" />
                    <p className="font-medium">{data.location || data.city}</p>
                  </div>
                )}
                {data.spotsAvailable && (
                  <div className="flex items-center gap-3">
                    <Users className="h-5 w-5 text-purple-600" />
                    <p className="font-medium">
                      {data.spotsAvailable} spots available
                      {data.registeredCount && ` (${data.registeredCount} registered)`}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Role Variants */}
            {(volunteering.variants?.length > 0 || volunteering.data?.variants?.length > 0) && (
              <VariantDisplay 
                variants={volunteering.variants?.length > 0 ? volunteering.variants : (volunteering.data?.variants || [])}
                variantCombinations={volunteering.variant_combinations?.length > 0 ? volunteering.variant_combinations : (volunteering.data?.variant_combinations || volunteering.data?.variantCombinations || [])}
                title="Volunteer Roles" 
                compact={true}
                basePrice={0}
                onSelectionChange={handleVariantSelectionChange}
              />
            )}

            {/* Impact Section */}
            {data.impact && (
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-start gap-3">
                    <Target className="h-5 w-5 text-green-500 mt-0.5" />
                    <div>
                      <h4 className="font-medium">Your Impact</h4>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap">{data.impact}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* CTA Buttons */}
            <div className="space-y-3">
              <Button className="w-full h-12 text-lg bg-pink-600 hover:bg-pink-700" disabled>
                <Heart className="h-5 w-5 mr-2" />
                VOLUNTEER NOW
              </Button>
              <Button variant="outline" className="w-full h-12 text-lg" disabled>
                <Briefcase className="h-5 w-5 mr-2" />
                LEARN MORE
              </Button>
              <p className="text-xs text-center text-gray-500">(Preview mode - Buttons disabled in admin view)</p>
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-3 gap-4 pt-4 border-t">
              <div className="text-center">
                <CheckCircle className="h-6 w-6 mx-auto text-gray-600 mb-1" />
                <p className="text-xs text-gray-600">Verified</p>
              </div>
              <div className="text-center">
                <Shield className="h-6 w-6 mx-auto text-gray-600 mb-1" />
                <p className="text-xs text-gray-600">Safe</p>
              </div>
              <div className="text-center">
                <Award className="h-6 w-6 mx-auto text-gray-600 mb-1" />
                <p className="text-xs text-gray-600">Certified</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default VolunteeringDetails;